import { LOCALE_CONFIG, requireLocale, type Locale } from "./locales";

// This is a bounded page-path contract, not the site's complete route inventory.
// Future routing must classify additional endpoints before integration.
const NON_PAGE_ROOTS = new Set([
  "api",
  "_next",
  "admin",
  "dev",
  "ops",
  "pdf",
  "pdf-live",
  "sitemap",
  "sitemap.xml",
  "robots.txt",
  "assets",
  "optimised",
  "fonts",
  "ics",
  "feed",
  "feed.xml",
  "rss",
  "rss.xml",
  "favicon.ico",
]);
const INTERNAL_LOCALE_ROOTS = new Set(["en", "zh-hans", "zh-hant"]);
const UNSAFE_SEGMENT = /[\s\u0000-\u001f\u007f-\u009f\\/?#%]/u;

export type ParsedPagePathname = Readonly<{
  locale: Locale;
  contentPathname: string;
}>;

function invalidPath(): never {
  throw new TypeError("Expected a canonical site-relative page pathname.");
}

function readSegments(value: unknown): { raw: string[]; decoded: string[] } {
  if (typeof value !== "string" || !value.startsWith("/")) invalidPath();
  if (value === "/") return { raw: [], decoded: [] };
  if (
    value.endsWith("/") ||
    /[\s\u0000-\u001f\u007f-\u009f\\?#]/u.test(value)
  ) {
    invalidPath();
  }
  const raw = value.slice(1).split("/");
  const decoded = raw.map((segment) => {
    if (!segment) invalidPath();
    let text: string;
    try {
      text = decodeURIComponent(segment);
    } catch {
      return invalidPath();
    }
    if (text === "." || text === ".." || UNSAFE_SEGMENT.test(text))
      invalidPath();
    return text;
  });
  return { raw, decoded };
}

function assertContentRoot(root: string | undefined): void {
  if (root === undefined) return;
  const lower = root.toLowerCase();
  if (
    lower === "zh" ||
    INTERNAL_LOCALE_ROOTS.has(lower) ||
    NON_PAGE_ROOTS.has(lower)
  ) {
    invalidPath();
  }
}

/** No query/hash handling, normalization, request state or publication inference. */
export function parsePublicPathname(value: unknown): ParsedPagePathname {
  const { raw, decoded } = readSegments(value);
  const first = decoded[0];
  if (first?.toLowerCase() === "zh") {
    // Encoded and uppercase prefix aliases must not create English/Chinese duplicates.
    if (raw[0] !== "zh") invalidPath();
    assertContentRoot(decoded[1]);
    return Object.freeze({
      locale: "zh-Hans",
      contentPathname: raw.length === 1 ? "/" : `/${raw.slice(1).join("/")}`,
    });
  }
  assertContentRoot(first);
  return Object.freeze({ locale: "en", contentPathname: value as string });
}

/** Build a candidate path, not permission to publish or offer a language link. */
export function localizePathname(
  contentPathname: unknown,
  locale: unknown
): string {
  const target = requireLocale(locale);
  const parsed = parsePublicPathname(contentPathname);
  if (parsed.locale !== "en") invalidPath();
  if (target === "en") return parsed.contentPathname;
  const suffix = parsed.contentPathname === "/" ? "" : parsed.contentPathname;
  return `${LOCALE_CONFIG[target].publicPrefix}${suffix}`;
}
